/**
 * Drag Handling Utilities
 *
 * This file contains utility functions for handling drag end events for various canvas elements
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

/**
 * Handle circle drag end
 * @param {string} id - Circle ID
 * @param {number} x - New X position
 * @param {number} y - New Y position
 * @param {Array} circles - Current circles array
 * @param {Function} setCircles - Setter for circles array
 * @param {Function} saveToHistory - Function to save to history
 */
export const createHandleCircleDragEnd =
  (setCircles, saveToHistory) => (id, x, y, circles) => {
    const updated = circles.map((circle) =>
      circle.id === id ? { ...circle, x, y } : circle
    );
    setCircles(updated);
    saveToHistory(); // Only after state updates
  };

/**
 * Handle arrow drag end
 * @param {string} id - Arrow ID
 * @param {number} x - New X position
 * @param {number} y - New Y position
 * @param {Array} arrows - Current arrows array
 * @param {Function} setArrows - Setter for arrows array
 * @param {Function} saveToHistory - Function to save to history
 * @param {Array} updatedArrowsOverride - Optional override array
 */
export const createHandleArrowDragEnd =
  (setArrows, saveToHistory) =>
  (id, x, y, arrows, updatedArrowsOverride = null) => {
    // Check if this is a restoration by looking at the arrow data
    const arrow = arrows.find((a) => a.id === id);
    const isRestoration = arrow?.isRestored || arrow?.isFromHistory;

    if (isRestoration) {
      return;
    }

    // If we have an override, use it (Canvas component already updated the state)
    const updated =
      updatedArrowsOverride ||
      arrows.map((arrow) => (arrow.id === id ? { ...arrow, x, y } : arrow));

    // Only update state if we don't have an override
    if (!updatedArrowsOverride) {
      setArrows(updated);
    }

    // Save to history immediately with the updated arrows array
    // Use requestAnimationFrame to ensure state has updated, but don't delay too long
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        saveToHistory("arrow_drag_end", null, updated);
      });
    });
  };

/**
 * Handle double-sided arrow drag end
 * @param {string} id - Double-sided arrow ID
 * @param {number} x - New X position
 * @param {number} y - New Y position
 * @param {Array} doubleSidedArrows - Current double-sided arrows array
 * @param {Function} setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {Function} saveToHistory - Function to save to history
 * @param {Array} updatedDoubleSidedArrowsOverride - Optional override array
 */
export const createHandleDoubleSidedArrowDragEnd =
  (setDoubleSidedArrows, saveToHistory) =>
  (id, x, y, doubleSidedArrows, updatedDoubleSidedArrowsOverride = null) => {
    // Check if this is a restoration by looking at the arrow data
    const arrow = doubleSidedArrows.find((a) => a.id === id);
    const isRestoration = arrow?.isRestored || arrow?.isFromHistory;

    if (isRestoration) {
      return;
    }

    // If we have an override, use it (Canvas component already updated the state)
    const updated =
      updatedDoubleSidedArrowsOverride ||
      doubleSidedArrows.map((arrow) =>
        arrow.id === id ? { ...arrow, x, y } : arrow
      );

    // Only update state if we don't have an override
    if (!updatedDoubleSidedArrowsOverride) {
      setDoubleSidedArrows(updated);
    }

    // Save to history after state update with proper timing
    setTimeout(() => {
      saveToHistory("double_sided_arrow_drag_end", null, null, updated);
    }, 50);
  };

/**
 * Handle line drag end
 * @param {Function} setLines - Setter for lines array
 * @param {Function} saveToHistory - Function to save to history
 * @param {Array} lines - Current lines array
 */
export const createHandleLineDragEnd =
  (setLines, saveToHistory, lines) =>
  (id, x, y, updatedLinesOverride = null) => {
    // If we have an override, use it (Canvas component already updated the state)
    const updated =
      updatedLinesOverride ||
      lines.map((line) => (line.id === id ? { ...line, x, y } : line));

    // Only update state if we don't have an override
    if (!updatedLinesOverride) {
      setLines(updated);
    }

    // Save to history after state update with proper timing
    setTimeout(() => {
      saveToHistory("line_drag_end", null, null, null, updated);
    }, 50);
  };

/**
 * Handle scribble drag end
 * @param {Function} setScribbles - Setter for scribbles array
 * @param {Function} saveToHistory - Function to save to history
 * @param {Array} scribbles - Current scribbles array
 */
export const createHandleScribbleDragEnd =
  (setScribbles, saveToHistory, scribbles) => (id, x, y) => {
    const updated = scribbles.map((scribble) =>
      scribble.id === id ? { ...scribble, x, y } : scribble
    );
    setScribbles(updated);

    // Save to history after state update with proper timing
    setTimeout(() => {
      saveToHistory("scribble_drag_end");
    }, 50);
  };

/**
 * Handle text drag end
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} saveToHistory - Function to save to history
 * @param {Array} texts - Current texts array
 */
export const createHandleTextDragEnd =
  (setTexts, setSelectedText, saveToHistory, texts) => (id, x, y) => {
    const updated = texts.map((text) =>
      text.id === id ? { ...text, x, y } : text
    );
    setTexts(updated);
    setSelectedText((prev) => ({ ...prev, x, y }));

    // Save to history after state update with proper timing
    setTimeout(() => {
      saveToHistory("text_drag_end");
    }, 50);
  };

/**
 * Walk from Konva event.target up the tree and stop any active drag.
 * Used when multi-select click removes an element from the selection: mousedown may have
 * started Konva DD; without this, dragMove keeps firing and group drag restarts.
 * @param {import("konva").KonvaEventObject<MouseEvent>} e
 */
export function stopKonvaDragFromPointerEvent(e) {
  let n = e?.target;
  while (n) {
    try {
      if (
        typeof n.isDragging === "function" &&
        n.isDragging() &&
        typeof n.stopDrag === "function"
      ) {
        n.stopDrag();
        return;
      }
    } catch (_err) {
      /* ignore */
    }
    n = typeof n.getParent === "function" ? n.getParent() : null;
  }
}
